import { useMemo } from "react"
import type { FC, ReactElement } from "react"

import { AnimatePresence, LazyMotion, domAnimation, motion } from "framer-motion"
import compact from "lodash-es/compact"
import concat from "lodash-es/concat"
import find from "lodash-es/find"
import isEmpty from "lodash-es/isEmpty"
import isString from "lodash-es/isString"
import map from "lodash-es/map"
import { ErrorBoundary } from "react-error-boundary"
import {
    type NavigateFunction,
    type Params,
    type SetURLSearchParams,
    useNavigate,
    useParams,
    useSearchParams
} from "react-router-dom"
import { useMount, useShallowCompareEffect } from "react-use"

import { ErrorHint, Loadable, RootLoaderFallback } from "@/3514/components"
import { ECoachTab, EPath } from "@/3514/constants"
import {
    EWindowCustomEvent,
    type TScreenBreakpoints,
    type TWindowCustomEventDispatcher,
    sessionTimeStateUtils,
    useScreenBreakpoints,
    useWindowCustomEventDispatcher
} from "@/3514/hooks"
import { type TCoachingSessionsGroupQuery, useCoachingSessionsGroupQuery } from "@/3514/queries"
import { useAnyModalVisible } from "@/3514/store/modal"
import { type ICoachingSessionModel } from "@/3514/types"
import { idleCb, minutes, seconds } from "@/3514/utils"

import classes from "./coach.group.styles"
import SessionsTab from "./coach.group.tab.sessions"
import SummaryTab from "./coach.group.tab.summary"
import { type TCoachGroupSessionNavigationConfig as TNavigationConfig } from "./coach.group.ui.types"

const CoachGroupPage: FC = (): ReactElement => {
    const navigate: NavigateFunction = useNavigate()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const dispatchWindowCustomEvent: TWindowCustomEventDispatcher<EWindowCustomEvent.CoachGroupMeta> =
        useWindowCustomEventDispatcher(EWindowCustomEvent.CoachGroupMeta)

    const [searchParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()

    const { groupId: groupIdParam }: Readonly<Params> = useParams()

    const sessionIdParam: string = searchParams.get("sessionId"),
        tabKey: string = searchParams.get("tab")

    const isAnyModalVisible: boolean = useAnyModalVisible()

    const {
        data: sessions,
        isSuccess: isSessionDataSuccess,
        isFetching: isSessionDataFetching
    }: TCoachingSessionsGroupQuery = useCoachingSessionsGroupQuery({
        refetchInterval: minutes(15),
        refetchOnWindowFocus: isAnyModalVisible ? true : "always",
        refetchOnMount: true,
        groupId: Number(groupIdParam),
        enabled: !!groupIdParam
    })

    const flatSessions: ICoachingSessionModel[] = useMemo<ICoachingSessionModel[]>(
        (): ICoachingSessionModel[] =>
            compact<ICoachingSessionModel>(
                concat<ICoachingSessionModel>(sessions?.liveSession, sessions?.pastSessions, sessions?.upcomingSessions)
            ),
        [sessions]
    )

    const currentSessionData: ICoachingSessionModel | undefined = find<ICoachingSessionModel>(flatSessions, {
        sessionId: Number(sessionIdParam)
    })

    useShallowCompareEffect((): void => {
        if (!isSessionDataSuccess) return

        function dispatcher(): void {
            const _firstSession: ICoachingSessionModel = find<ICoachingSessionModel>(
                flatSessions,
                (s: ICoachingSessionModel): boolean => isString(s.groupName) && isString(s.cohortName)
            )

            dispatchWindowCustomEvent({
                groupName: _firstSession.groupName,
                cohortName: _firstSession.cohortName
            })
        }

        if (breakpoints.xs || breakpoints.sm) requestAnimationFrame((): void => dispatcher())
        else idleCb((): void => dispatcher(), seconds(10))
    }, [isSessionDataSuccess, flatSessions, dispatchWindowCustomEvent])

    const currentNavigationConfig: TNavigationConfig[] = useMemo<TNavigationConfig[]>(
        (): TNavigationConfig[] =>
            map<ICoachingSessionModel, TNavigationConfig>(
                flatSessions,
                (s: ICoachingSessionModel): TNavigationConfig => ({
                    id: s.sessionId,
                    date: s.sessionTime,
                    isUpcoming: sessionTimeStateUtils.getIsSessionUpcoming({
                        startDate: s.sessionTime,
                        durationInMins: s.sessionDuration
                    }),
                    isLive: sessionTimeStateUtils.getIsSessionLive({
                        startDate: s.sessionTime,
                        durationInMins: s.sessionDuration
                    })
                })
            ),
        [flatSessions]
    )

    const isSummaryTab: boolean = tabKey === ECoachTab.Group.Summary
    const isSessionsTab: boolean = tabKey === ECoachTab.Group.Sessions

    useMount((): void => {
        if (isEmpty(groupIdParam)) navigate(EPath.Coach_Dashboard({ tab: "Sessions" }), { replace: true })
    })

    return (
        <ErrorBoundary
            resetKeys={[groupIdParam, sessionIdParam, tabKey]}
            fallback={<ErrorHint isVisible containerClassName={classes.page.error()} />}
        >
            <LazyMotion features={domAnimation}>
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={tabKey}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="flex-1 flex flex-col"
                    >
                        {isSummaryTab && (
                            <Loadable
                                isLoading={isSessionDataFetching}
                                isReady={isSessionDataSuccess}
                                component={SummaryTab}
                                componentProps={{
                                    session: currentSessionData,
                                    navigationConfig: currentNavigationConfig
                                }}
                                loadingFallback={<RootLoaderFallback top="25%" xsTop="45%" />}
                            />
                        )}

                        {isSessionsTab && <SessionsTab />}
                    </motion.div>
                </AnimatePresence>
            </LazyMotion>
        </ErrorBoundary>
    )
}

CoachGroupPage.displayName = "CoachGroupPage"

export { CoachGroupPage }
